import type { ReactNode } from 'react';
import { Outlet, Link, createFileRoute, redirect } from '@tanstack/react-router';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { StepUpModal } from '@/components/auth/StepUpModal';
import { useAuthStore } from '@/stores/auth';
import { refreshToken } from '@/lib/auth';
import { useAdminSecurityAlerts } from '@/hooks/useAdminSecurityAlerts';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { FileQuestion, AlertTriangle, Home, RotateCcw } from 'lucide-react';

const USE_COOKIES = import.meta.env.VITE_ADMIN_AUTH_COOKIES === 'true';

export const Route = createFileRoute('/_admin')({
  component: AdminLayout,
  notFoundComponent: AdminNotFound,
  errorComponent: AdminRouteError,
  beforeLoad: async () => {
    const state = useAuthStore.getState();
    const session = state.session;

    if (!session) {
      throw redirect({ to: '/login' });
    }

    // Cookie mode: tokens vivem no backend. Valida com /auth/admin/me em vez
    // de confiar em localStorage (que pode estar "fantasma" se cookies foram
    // revogados/expirados sem limpar zustand). Evita loop /login<->/dashboard.
    if (USE_COOKIES) {
      try {
        await api.get('/auth/admin/me');
        return;
      } catch {
        // 401/403 → cookie invalido. Limpa localStorage e manda pro login.
        state.logout();
        throw redirect({ to: '/login' });
      }
    }

    // Legacy Bearer mode — enforce client-side token expiry + manual refresh.
    if (session.tokenExpiresAt && session.tokenExpiresAt > Date.now()) {
      return;
    }

    if (session.refreshToken) {
      try {
        const result = await refreshToken(session.refreshToken);
        if (result.success && result.token) {
          state.setSession({
            ...session,
            token: result.token,
            refreshToken: result.refreshToken || session.refreshToken,
            tokenExpiresAt: Date.now() + (result.expiresIn ?? 7 * 24 * 3600) * 1000,
          });
          return;
        }
      } catch {
        // Refresh failed — redirect to login
      }
    }

    state.logout();
    throw redirect({ to: '/login' });
  },
});

function AdminLayout() {
  // EPIC-RN-12: subscribe to admin realtime events (security:alert, admin:ops).
  // Mounted uma vez aqui garante toasts em todas as rotas _admin.
  useAdminSecurityAlerts();

  return (
    <div className="flex h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-auto bg-muted/20 p-6">
          <Outlet />
        </main>
      </div>
      {/* Step-up modal global — exibido on-demand pelo useStepUpStore.openStepUpModal */}
      <StepUpModal />
    </div>
  );
}

// Renderizados DENTRO do <main> do layout (a sidebar/topbar permanecem),
// então o admin continua navegando sem precisar do Back do browser.
function AdminStatePanel({
  icon,
  title,
  message,
  children,
}: {
  icon: ReactNode;
  title: string;
  message: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="mb-4 text-muted-foreground">{icon}</div>
      <h1 className="mb-2 text-2xl font-semibold">{title}</h1>
      <p className="mb-6 max-w-md text-sm text-muted-foreground">{message}</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {children}
        <Button asChild variant="outline">
          <Link to="/dashboard">
            <Home className="mr-2 h-4 w-4" />
            Ir para o Dashboard
          </Link>
        </Button>
      </div>
    </div>
  );
}

function AdminNotFound() {
  return (
    <AdminStatePanel
      icon={<FileQuestion className="h-12 w-12" />}
      title="Página não encontrada"
      message="A página que você tentou acessar não existe ou foi movida. Use o menu lateral para continuar navegando."
    />
  );
}

function AdminRouteError({ error, reset }: { error: Error; reset?: () => void }) {
  return (
    <AdminStatePanel
      icon={<AlertTriangle className="h-12 w-12 text-amber-500" />}
      title="Algo deu errado"
      message={error?.message || 'Ocorreu um erro ao carregar esta página. Você pode tentar novamente ou navegar para outra seção.'}
    >
      {reset && (
        <Button onClick={() => reset()}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Tentar novamente
        </Button>
      )}
    </AdminStatePanel>
  );
}
